import { ExistingDebtBuilder } from '~/builders/existing-debt/ExistingDebtBuilder';
import { getFirstFitCandidates } from '~/engine/millage/shape-generators';
import { CandidateProcessor } from '~/engine/processing/managers/candidate-processor';
import { BondCategorizationEngine } from '~/engine/processing/shared/bond-categorization';
import { CandidateNaming } from '~/engine/processing/shared/candidate-naming';
import { logger } from '~/services/logging';
import type {
  BaseProjectElements,
  BondSeries,
  Candidate,
  Configuration,
  MillageInformation,
  ProcessingPhase,
  Project,
} from '~/types';

// Counter for generating unique candidate IDs within the same millisecond
let candidateIdCounter = 0;

/**
 * Determines the distribution strategy for a candidate based on bond series configuration.
 */
function determineDistributionStrategy(
  bondSeries: BondSeries[]
): 'unlimited' | 'all-targeted' | 'mixed-targeting' {
  // Use the BondCategorizationEngine to determine the correct strategy
  return BondCategorizationEngine.getDistributionStrategy(bondSeries);
}

/**
 * Creates base project elements from project data
 */
export function createBaseProjectElements(project: Project): BaseProjectElements {
  // Validate that all required data exists
  // These validations prevent silent failures in candidate processing
  if (!project.dateElements?.dateRecordsMapByPaymentDate) {
    throw new Error(
      'Cannot create BaseProjectElements: dateRecordsMapByPaymentDate is required. ' +
        'Ensure date maps are built in ProjectSetupPage before processing candidates. ' +
        'The calculation engine needs these maps for revenue and cash flow calculations.'
    );
  }

  if (!project.dateElements?.dateRecordsMapByTaxYear) {
    throw new Error(
      'Cannot create BaseProjectElements: dateRecordsMapByTaxYear is required. ' +
        'Ensure date maps are built in ProjectSetupPage before processing candidates.'
    );
  }

  if (!project.dateElements?.dateRecords || project.dateElements.dateRecords.length === 0) {
    throw new Error(
      'Cannot create BaseProjectElements: dateRecords are required and must not be empty. ' +
        'Ensure dates are configured in ProjectSetupPage.'
    );
  }

  if (!project.taxableValueMap || Object.keys(project.taxableValueMap).length === 0) {
    throw new Error(
      'Cannot create BaseProjectElements: taxableValueMap is required and must not be empty. ' +
        'Ensure taxable values are configured in ProjectSetupPage.'
    );
  }

  const existingDebtProjects = project.existingDebtProjects || [];
  const combinedDebtService =
    existingDebtProjects.length > 0
      ? ExistingDebtBuilder.combineDebtProjects(existingDebtProjects)
      : {
          id: 'combined',
          name: 'Combined Debt Service',
          existingDebtPaymentRecords: [],
          payments: [],
          totalPrincipal: 0,
          totalInterest: 0,
          totalPayments: 0,
        };

  // Calculate total existing debt once here
  const totalExistingDebt =
    combinedDebtService.existingDebtPaymentRecords?.reduce(
      (sum, payment) => sum + payment.totalPayment,
      0
    ) || 0;

  return {
    dateRecords: project.dateElements.dateRecords,
    dateRecordsMapByPaymentDate: project.dateElements.dateRecordsMapByPaymentDate,
    dateRecordsMapByTaxYear: project.dateElements.dateRecordsMapByTaxYear,
    taxableValueMap: project.taxableValueMap,
    existingDebtInformation: {
      existingDebtProjects,
      combinedDebtService,
      totalExistingDebt,
    },
  };
}

// calculateCandidateResults function moved to ~/candidate-processing/engines/forward-completion/results-evaluator.ts

/**
 * Creates a candidate from millage information
 */
export function createCandidateFromMillage(
  project: Project,
  configuration: Configuration,
  millageInfo: MillageInformation,
  namePrefix: 'FirstFit' | 'BestFit' | 'SingleTest' | 'ShapeOpt',
  phase: ProcessingPhase
): Candidate {
  // Note: DRF information is now accessed via configuration.drfInputs (not stored in candidate)

  // Validate millageInfo structure
  if (!millageInfo || !millageInfo.millageShape || !millageInfo.millageShape.shapeId) {
    throw new Error(
      `Invalid millageInfo structure: millageShape or shapeId is missing. ` +
        `Received: ${JSON.stringify(millageInfo ? Object.keys(millageInfo) : 'null')}`
    );
  }

  // Use new naming convention: {shapeId}.0 for base candidates
  const shapeId = millageInfo.millageShape.shapeId;
  const candidateName = CandidateNaming.createBaseName(shapeId);

  // Generate unique candidate ID using timestamp + counter + random UUID
  // Counter ensures uniqueness even when multiple candidates are created in the same millisecond
  candidateIdCounter += 1;
  const candidate: Candidate = {
    id: `candidate-${Date.now()}-${candidateIdCounter}-${crypto.randomUUID().slice(0, 8)}`,
    name: candidateName,
    description: shapeId,
    isProcessed: false,
    createdInPhase: phase,
    // Removed: baseProjectElements (project data accessed via project parameter)
    // Removed: drfInformation (configuration data accessed via configuration parameter)
    // Removed: bondSeries (configuration data accessed via configuration parameter)
    millageInformation: millageInfo,
    revenueMap: {},
    cashFlows: [],
    distributionStrategy: determineDistributionStrategy(configuration.bondSeries),
    // Initialize optimization history with the initial shape entry
    optimizationHistory: [
      {
        type: 'initial-shape',
        description: `Created with shape: ${shapeId}`,
        timestamp: new Date().toISOString(),
        details: {
          shapeId: shapeId,
          processType: namePrefix,
        },
      },
    ],
    results: {
      isViable: true,
      failureMessages: [],
      totalRevenue: 0,
      totalInterest: 0,
      totalPrincipal: 0,
      revenueUtilization: 0,
      interestRatio: 0,
      efficiency: 0,
      bondSeriesResults: [],
    },
  };

  // Process the candidate - CandidateProcessor handles all processing including targeted bonds
  const result = CandidateProcessor.processCandidate(candidate, project, configuration);

  return result.candidate;
}

/**
 * Builds first-fit candidates for a project
 */
export function buildFirstFitCandidates(
  project: Project,
  configuration: Configuration
): Candidate[] {
  const baseElements = createBaseProjectElements(project);

  // Create simplified project data for millage processing
  const projectData = {
    dateRecords: baseElements.dateRecords,
    dateRecordsMapByPaymentDate: baseElements.dateRecordsMapByPaymentDate,
    taxableValueMap: baseElements.taxableValueMap,
    dateInputs: project.dateInputs
      ? {
          startingTaxYear: parseInt(project.dateInputs.startingTaxYear, 10),
        }
      : undefined,
  };

  // Get millage shapes
  logger.L3.debug(`Getting millage shapes for project`, {
    hasDateRecords: baseElements.dateRecords.length > 0,
    hasDateRecordsMap: Object.keys(baseElements.dateRecordsMapByPaymentDate).length > 0,
    hasTaxableValues: Object.keys(baseElements.taxableValueMap).length > 0,
    bondSeriesCount: configuration.bondSeries.length,
  });

  const millageShapes: MillageInformation[] = getFirstFitCandidates(projectData, configuration);

  logger.L3.info(`Generated millage shapes`, {
    count: millageShapes.length,
  });

  // Create candidates from millage shapes
  const candidates = millageShapes.map((shape) =>
    createCandidateFromMillage(project, configuration, shape, 'FirstFit', 'first-fit')
  );

  logger.L3.info(`Created first-fit candidates`, {
    count: candidates.length,
  });

  candidates.forEach((candidate, index) => {
    logger.L3.debug(`Candidate details`, {
      index,
      cashFlowCount: candidate.cashFlows.length,
      revenueMapKeys: Object.keys(candidate.revenueMap).length,
      isProcessed: candidate.isProcessed,
    });
  });

  return candidates;
}
